/**
 * DecisionFlow — RankingManager
 * ui/src/components/tenant-admin/RankingManager.jsx
 *
 * Domain: §P4-08 Ranking Plans
 * Embedded in: PlanDetailView modifier card grid (Pattern A)
 * API:
 *   GET  /api/v1/ranking-plans?plan_component_id={id}
 *   POST /api/v1/ranking-plans
 *   GET  /api/v1/ranking-plans/{id}/rows
 *   POST /api/v1/ranking-plans/{id}/rows          (upsert payout rows)
 *   POST /api/v1/ranking-plans/{id}/approve
 *   GET  /api/v1/ranking-results?calculation_run_id={id}  (read-only results)
 * Feature gate: Enterprise — ranking_plans
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner, no content rendered.
 * Tabs: Plan | Payout Rows | Results (read-only)
 * State derived from approved_by / submitted_at — no status column.
 * planComponentId prop is required — all API calls scoped to it.
 * Path: ui/src/components/tenant-admin/RankingManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'plan',     label: 'Plan'        },
  { key: 'rows',     label: 'Payout Rows' },
  { key: 'results',  label: 'Results'     },
];

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_PLAN = {
  ranking_plan_name: '',
  ranking_method:    'percentile',
  metric:            '',
  effective_date:    '',
  expiration_date:   '',
};

const EMPTY_ROW = {
  rank_from:       '',
  rank_to:         '',
  payout_pct:      '',
  payout_amount:   '',
};

const RANKING_METHODS = [
  { value: 'percentile', label: 'Percentile' },
  { value: 'rank',       label: 'Absolute Rank' },
  { value: 'decile',     label: 'Decile' },
];

// ---------------------------------------------------------------------------
// RankingManager
// ---------------------------------------------------------------------------

export default function RankingManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.ranking')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [gated, setGated]                   = useState(false);
  const [activeTab, setActiveTab]           = useState('plan');

  // Plan tab state
  const [plans, setPlans]                   = useState([]);
  const [plansLoading, setPlansLoading]     = useState(false);
  const [plansError, setPlansError]         = useState('');
  const [selectedPlan, setSelectedPlan]     = useState(null);
  const [showPlanForm, setShowPlanForm]     = useState(false);
  const [planForm, setPlanForm]             = useState(EMPTY_PLAN);
  const [planSaving, setPlanSaving]         = useState(false);
  const [planFormError, setPlanFormError]   = useState('');
  const [actionLoading, setActionLoading]   = useState(false);
  const [actionError, setActionError]       = useState('');

  // Rows tab state
  const [rows, setRows]                     = useState([]);
  const [rowsLoading, setRowsLoading]       = useState(false);
  const [rowsError, setRowsError]           = useState('');
  const [showRowForm, setShowRowForm]       = useState(false);
  const [rowForm, setRowForm]               = useState(EMPTY_ROW);
  const [rowSaving, setRowSaving]           = useState(false);
  const [rowFormError, setRowFormError]     = useState('');

  // Results tab state
  const [results, setResults]               = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError]     = useState('');

  const [toast, setToast]                   = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load plans — enterprise gate check first
  // ---------------------------------------------------------------------------

  const loadPlans = useCallback(async () => {
    if (!user || !planComponentId) return;
    setPlansLoading(true);
    setPlansError('');
    setGated(false);
    try {
      const data = await apiGet(`/api/v1/ranking-plans?plan_component_id=${planComponentId}`);
      setPlans(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'FEATURE_UNAVAILABLE') {
        setGated(true);
      } else {
        setPlansError(getErrorMessage(error, 'Unable to load ranking plans.'));
      }
    } finally {
      setPlansLoading(false);
    }
  }, [user, planComponentId]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  // ---------------------------------------------------------------------------
  // Load payout rows for selected plan
  // ---------------------------------------------------------------------------

  const loadRows = useCallback(async (planId) => {
    if (!planId) return;
    setRowsLoading(true);
    setRowsError('');
    try {
      const data = await apiGet(`/api/v1/ranking-plans/${planId}/rows`);
      setRows(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setRowsError(getErrorMessage(error, 'Unable to load payout rows.'));
    } finally {
      setRowsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'rows' && selectedPlan) {
      loadRows(selectedPlan.ranking_plan_id);
    }
  }, [activeTab, selectedPlan, loadRows]);

  // ---------------------------------------------------------------------------
  // Load results (read-only)
  // ---------------------------------------------------------------------------

  const loadResults = useCallback(async () => {
    if (!selectedPlan) return;
    setResultsLoading(true);
    setResultsError('');
    try {
      // Results are indexed by calculation_run_id; without a specific run,
      // fetch latest by plan_component_id as a fallback scope.
      const data = await apiGet(`/api/v1/ranking-results?plan_component_id=${planComponentId}`);
      setResults(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setResultsError(getErrorMessage(error, 'Unable to load ranking results.'));
    } finally {
      setResultsLoading(false);
    }
  }, [selectedPlan, planComponentId]);

  useEffect(() => {
    if (activeTab === 'results') {
      loadResults();
    }
  }, [activeTab, loadResults]);

  // ---------------------------------------------------------------------------
  // Create plan
  // ---------------------------------------------------------------------------

  async function handleCreatePlan(e) {
    e.preventDefault();
    if (!planForm.ranking_plan_name.trim() || !planForm.metric.trim()) {
      setPlanFormError('Plan name and metric are required.');
      return;
    }
    setPlanSaving(true);
    setPlanFormError('');
    try {
      await apiPost('/api/v1/ranking-plans', {
        plan_component_id:  planComponentId,
        ranking_plan_name:  planForm.ranking_plan_name.trim(),
        ranking_method:     planForm.ranking_method,
        metric:             planForm.metric.trim(),
        effective_date:     planForm.effective_date || null,
        expiration_date:    planForm.expiration_date || null,
      });
      setShowPlanForm(false);
      setPlanForm(EMPTY_PLAN);
      showToast('Ranking plan created.');
      await loadPlans();
    } catch (error) {
      setPlanFormError(getErrorMessage(error, 'Failed to create ranking plan.'));
    } finally {
      setPlanSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Submit / Approve plan
  // ---------------------------------------------------------------------------

  async function handleSubmit() {
    if (!selectedPlan) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/ranking-plans/${selectedPlan.ranking_plan_id}/submit`);
      showToast('Submitted for approval.');
      await loadPlans();
      setSelectedPlan((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to submit for approval.'));
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApprove() {
    if (!selectedPlan) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/ranking-plans/${selectedPlan.ranking_plan_id}/approve`);
      showToast('Ranking plan approved.');
      await loadPlans();
      setSelectedPlan((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setActionError('Self-approval is not permitted (NN-10).');
      } else {
        setActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Upsert payout row
  // ---------------------------------------------------------------------------

  async function handleUpsertRow(e) {
    e.preventDefault();
    if (!selectedPlan) {
      setRowFormError('Select a ranking plan in the Plan tab first.');
      return;
    }
    if (!rowForm.rank_from || !rowForm.rank_to) {
      setRowFormError('Rank from and rank to are required.');
      return;
    }
    setRowSaving(true);
    setRowFormError('');
    try {
      await apiPost(`/api/v1/ranking-plans/${selectedPlan.ranking_plan_id}/rows`, {
        rank_from:     parseInt(rowForm.rank_from, 10),
        rank_to:       parseInt(rowForm.rank_to, 10),
        payout_pct:    rowForm.payout_pct ? parseFloat(rowForm.payout_pct) : null,
        payout_amount: rowForm.payout_amount ? parseFloat(rowForm.payout_amount) : null,
      });
      setShowRowForm(false);
      setRowForm(EMPTY_ROW);
      showToast('Payout row saved.');
      await loadRows(selectedPlan.ranking_plan_id);
    } catch (error) {
      setRowFormError(getErrorMessage(error, 'Failed to save payout row.'));
    } finally {
      setRowSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // Enterprise gate — checked before auth guard per render order spec
  if (gated) {
    return (
      <section className="flex flex-col gap-4">
        <GateStatusBanner feature="ranking_plans" />
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">

      {readOnly && (
        <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Plan is approved — configuration is read-only.
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-tok-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Plan tab ── */}
      {activeTab === 'plan' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Ranking Plans</h2>
            {!readOnly && (
              <button
                type="button"
                onClick={() => { setShowPlanForm((v) => !v); setPlanFormError(''); setPlanForm(EMPTY_PLAN); }}
                className={btnPrimary}
              >
                {showPlanForm ? 'Cancel' : '+ Add Plan'}
              </button>
            )}
          </div>

          {showPlanForm && (
            <form
              onSubmit={handleCreatePlan}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
            >
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Ranking Plan</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className={labelClass + ' sm:col-span-2'}>
                  Plan Name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input
                    type="text"
                    value={planForm.ranking_plan_name}
                    onChange={(e) => setPlanForm((f) => ({ ...f, ranking_plan_name: e.target.value }))}
                    placeholder="e.g. Q1 Regional Ranking"
                    className={inputClass}
                    required
                  />
                </label>
                <label className={labelClass}>
                  Ranking Method
                  <select
                    value={planForm.ranking_method}
                    onChange={(e) => setPlanForm((f) => ({ ...f, ranking_method: e.target.value }))}
                    className={inputClass}
                  >
                    {RANKING_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </label>
                <label className={labelClass}>
                  Metric <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input
                    type="text"
                    value={planForm.metric}
                    onChange={(e) => setPlanForm((f) => ({ ...f, metric: e.target.value }))}
                    placeholder="e.g. revenue, units_sold"
                    className={inputClass}
                    required
                  />
                </label>
                <label className={labelClass}>
                  Effective Date
                  <input
                    type="date"
                    value={planForm.effective_date}
                    onChange={(e) => setPlanForm((f) => ({ ...f, effective_date: e.target.value }))}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Expiration Date
                  <input
                    type="date"
                    value={planForm.expiration_date}
                    onChange={(e) => setPlanForm((f) => ({ ...f, expiration_date: e.target.value }))}
                    className={inputClass}
                  />
                </label>
              </div>
              {planFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                  {planFormError}
                </div>
              )}
              <div className="flex gap-2">
                <button type="submit" disabled={planSaving} className={btnPrimary}>
                  {planSaving ? 'Saving…' : 'Create Plan'}
                </button>
                <button type="button" onClick={() => { setShowPlanForm(false); setPlanFormError(''); }} className={btnSecondary}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {plansLoading && (
            <div className="animate-pulse space-y-2">
              <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
              <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
            </div>
          )}
          {plansError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {plansError}
            </div>
          )}
          {!plansLoading && !plansError && plans.length === 0 && (
            <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No ranking plans configured</p>
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Add a ranking plan to configure rank-based payout tiers.</p>
            </div>
          )}
          {plans.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Plan Name</th>
                    <th className={thClass}>Method</th>
                    <th className={thClass}>Metric</th>
                    <th className={thClass}>Effective</th>
                    <th className={thClass}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => (
                    <tr
                      key={p.ranking_plan_id}
                      className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedPlan?.ranking_plan_id === p.ranking_plan_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                      onClick={() => { setSelectedPlan(p); setActionError(''); }}
                    >
                      <td className={tdClass + ' font-medium'}>{p.ranking_plan_name}</td>
                      <td className={tdClass}>{p.ranking_method ?? '—'}</td>
                      <td className={tdClass}>{p.metric ?? '—'}</td>
                      <td className={tdClass}>{p.effective_date ?? '—'}</td>
                      <td className={tdClass}><StatusPill status={deriveState(p)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Approval panel for selected plan */}
          {selectedPlan && !readOnly && (() => {
            const state = deriveState(selectedPlan);
            return (
              <div className="flex flex-col gap-3">
                <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Selected: <strong className="text-[rgb(var(--color-text))]">{selectedPlan.ranking_plan_name}</strong>
                </div>
                {actionError && (
                  <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                    {actionError}
                  </div>
                )}
                <ApprovalPanel
                  variant="ranking_plan"
                  currentState={state}
                  submittedBy={selectedPlan.submitted_by}
                  currentUserId={currentUserId}
                  onSubmit={state === 'draft' ? handleSubmit : null}
                  onApprove={state === 'pending_approval' ? handleApprove : null}
                  onReject={null}
                  approvedBy={selectedPlan.approved_by}
                  approvedAt={selectedPlan.approved_at}
                  loading={actionLoading}
                />
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Payout Rows tab ── */}
      {activeTab === 'rows' && (
        <div className="flex flex-col gap-4">
          {!selectedPlan && (
            <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
              Select a ranking plan in the Plan tab first.
            </div>
          )}

          {selectedPlan && (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Payout Rows</h2>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan: {selectedPlan.ranking_plan_name}</p>
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => { setShowRowForm((v) => !v); setRowFormError(''); setRowForm(EMPTY_ROW); }}
                    className={btnPrimary}
                  >
                    {showRowForm ? 'Cancel' : '+ Add Row'}
                  </button>
                )}
              </div>

              {showRowForm && (
                <form
                  onSubmit={handleUpsertRow}
                  className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
                >
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">Add / Update Payout Row</p>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Existing rows for the same rank range will be overwritten (upsert).</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <label className={labelClass}>
                      Rank From <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={rowForm.rank_from}
                        onChange={(e) => setRowForm((f) => ({ ...f, rank_from: e.target.value }))}
                        placeholder="1"
                        className={inputClass}
                        required
                      />
                    </label>
                    <label className={labelClass}>
                      Rank To <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={rowForm.rank_to}
                        onChange={(e) => setRowForm((f) => ({ ...f, rank_to: e.target.value }))}
                        placeholder="5"
                        className={inputClass}
                        required
                      />
                    </label>
                    <label className={labelClass}>
                      Payout %
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={rowForm.payout_pct}
                        onChange={(e) => setRowForm((f) => ({ ...f, payout_pct: e.target.value }))}
                        placeholder="0.00"
                        className={inputClass}
                      />
                    </label>
                    <label className={labelClass}>
                      Payout Amount
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={rowForm.payout_amount}
                        onChange={(e) => setRowForm((f) => ({ ...f, payout_amount: e.target.value }))}
                        placeholder="0.00"
                        className={inputClass}
                      />
                    </label>
                  </div>
                  {rowFormError && (
                    <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                      {rowFormError}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button type="submit" disabled={rowSaving} className={btnPrimary}>
                      {rowSaving ? 'Saving…' : 'Save Row'}
                    </button>
                    <button type="button" onClick={() => { setShowRowForm(false); setRowFormError(''); }} className={btnSecondary}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {rowsLoading && (
                <div className="animate-pulse space-y-2">
                  <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
                </div>
              )}
              {rowsError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {rowsError}
                </div>
              )}
              {!rowsLoading && !rowsError && rows.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No payout rows. Add rank tiers above.</p>
                </div>
              )}
              {rows.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Rank From</th>
                        <th className={thClass}>Rank To</th>
                        <th className={thClass}>Payout %</th>
                        <th className={thClass}>Payout Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.ranking_plan_row_id ?? i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{r.rank_from ?? '—'}</td>
                          <td className={tdClass}>{r.rank_to ?? '—'}</td>
                          <td className={tdClass}>{r.payout_pct != null ? `${r.payout_pct}%` : '—'}</td>
                          <td className={tdClass}>{r.payout_amount != null ? Number(r.payout_amount).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Results tab (read-only, engine-written) ── */}
      {activeTab === 'results' && (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Results are engine-written and read-only. Run a calculation to populate.
          </div>

          {resultsLoading && (
            <div className="animate-pulse space-y-2">
              <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
            </div>
          )}
          {resultsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {resultsError}
            </div>
          )}
          {!resultsLoading && !resultsError && results.length === 0 && (
            <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No ranking results for this period. Run a calculation to generate results.</p>
            </div>
          )}
          {results.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Position</th>
                    <th className={thClass}>Rank</th>
                    <th className={thClass}>Metric Value</th>
                    <th className={thClass}>Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={r.ranking_result_id ?? i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass}>{r.position_id ?? '—'}</td>
                      <td className={tdClass + ' font-medium'}>{r.rank ?? '—'}</td>
                      <td className={tdClass}>{r.metric_value != null ? Number(r.metric_value).toLocaleString() : '—'}</td>
                      <td className={tdClass}>{r.payout_amount != null ? Number(r.payout_amount).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg"
        >
          {toast}
        </div>
      )}
    </section>
  );
}
